// SPDX-License-Identifier: BUSL-1.1
/**
 * Zod schema for GateRule validation.
 *
 * Validates gate rule configuration loaded from YAML/JSON config files.
 * Ensures patterns are non-empty, gates are valid PolicyGate values,
 * and priority is a finite number.
 *
 * Note: avoids z.default() and z.infer because the ambient zod-shim.d.ts
 * (used during partial installs) does not declare them. Types are defined
 * explicitly instead.
 *
 */
import { z } from 'zod';
/** Schema for a single gate rule. */
export const GateRuleSchema = z.object({
    toolPattern: z.string().min(1),
    gate: z.enum(['safe', 'guarded', 'manual']),
    environment: z.string().min(1).optional(),
    actorType: z.enum(['human', 'agent', 'system']).optional(),
    priority: z.number().optional(),
});
/** Schema for the full gate engine config. */
export const GateEngineConfigSchema = z.object({
    defaultGate: z.enum(['safe', 'guarded', 'manual']),
    rules: z.array(GateRuleSchema).optional(),
    environment: z.string().min(1).optional(),
});
/**
 * Parse and validate gate engine configuration.
 *
 * Throws on invalid input with detailed field-level messages.
 * Uses safeParse internally (shim-compatible — the ambient zod-shim.d.ts
 * only exposes safeParse, not parse).
 */
export function parseGateEngineConfig(input) {
    const result = GateEngineConfigSchema.safeParse(input);
    if (!result.success) {
        const messages = result.error.issues.map((issue) => {
            const path = issue.path.join('.');
            return path ? `${path}: ${issue.message}` : issue.message;
        });
        throw new Error(`Invalid gate engine config: ${messages.join('; ')}`);
    }
    return result.data;
}
/**
 * Safe parse — returns success/error without throwing.
 */
export function safeParseGateEngineConfig(input) {
    const result = GateEngineConfigSchema.safeParse(input);
    if (result.success) {
        return { success: true, data: result.data };
    }
    return { success: false, error: result.error };
}
//# sourceMappingURL=gate-rules-schema.js.map